import React from 'react';
import { OperationBC, OperationFormulaire } from '../types';
import OperationFormulaireAdmin from './OperationFormulaireAdmin';
import { adresseEntiteUneLigne } from '../../../utils/adresse';
import './DescriptifAdministratifBC.css';

// Descriptif administratif acheteur-BC, structure/titres/layout IDENTIQUES a la refonte MS
// (OperationDescriptifAdministratif MS). ISOLE : copie co-localisee, aucun import depuis
// SuiviOperationMSV2 ; grille .bcadm-desc__* (copiee/renommee depuis .opms__*).
//
// 3 sous-sections :
//  - Acheteur : recap organisme (lecture seule) + signataire / modalite de facturation (saisis).
//  - Operation : OperationFormulaireAdmin (base + complements nom_ouvrage/pays/moe/phases).
//  - Particularites administratives : 5 champs (la visite reste dans le bloc Operation cote BC).

const PHRASE_MAX = 255;
const PARTICULARITES_MAX = 600;

interface Props {
  operation: OperationBC;
  formData: OperationFormulaire;
  viewMode: boolean;
  errors?: Record<string, string>;
  setFormData?: (data: OperationFormulaire) => void;
}

// Case de recap LECTURE SEULE (libelle + valeur, "-" si absent), span de colonnes.
const Champ: React.FC<{ label: string; valeur?: unknown; col: number }> = ({ label, valeur, col }) => {
  const texte = typeof valeur === 'string' || typeof valeur === 'number' ? String(valeur).trim() : '';
  return (
    <div className="form-group" style={{ gridColumn: `span ${col}` }}>
      <label>{label}</label>
      <div className="input-inactif">{texte || '-'}</div>
    </div>
  );
};

// Cles du formulaire SAISIES dans ce bloc. Les 4 champs de contact operationnel s'y sont ajoutes :
// ils etaient auparavant deduits du compte acheteur et affiches en lecture seule, ce qui interdisait
// de designer un interlocuteur different pour une operation donnee.
type CleSaisie =
  | 'signataire'
  | 'modaliteFacturation'
  | 'contactNom'
  | 'contactFonction'
  | 'contactMail'
  | 'contactTelephone';

// --- Sous-bloc "Acheteur" : recap organisme + signataire / modalite (saisis) -------------------
const AcheteurRecap: React.FC<Props> = ({ operation, formData, viewMode, errors = {}, setFormData }) => {
  const org = operation.organisme;
  const adr = org?.adresse;
  // Adresse du siege social sur UNE ligne, code postal / ville / pays compris : ils n'ont plus de
  // champ propre a cote. Meme forme que l'adresse de l'operation en lecture seule, pour que les deux
  // adresses de l'onglet se lisent pareil.
  const adresseLigne = adresseEntiteUneLigne(adr);

  const update = (cle: CleSaisie, value: string) => {
    if (setFormData) setFormData({ ...formData, [cle]: value });
  };

  const champSaisi = (cle: CleSaisie, label: string, placeholder: string, col = 6) => {
    const valeur = String(formData[cle] ?? '');
    return (
      <div className="form-group" style={{ gridColumn: `span ${col}` }} key={cle}>
        <label>{label}</label>
        {viewMode ? (
          <div className="input-inactif">{valeur || '-'}</div>
        ) : (
          <>
            <input
              type="text"
              maxLength={PHRASE_MAX}
              value={valeur}
              onChange={(e) => update(cle, e.target.value)}
              placeholder={placeholder}
              className={errors[cle] ? 'error' : ''}
            />
            <span className="bcadm-char-count">{valeur.length}/{PHRASE_MAX}</span>
            {/* L'erreur DOIT etre rendue : `scrollToFirstError` cible `.error-message`, et une regle
                de validation sans rendu rend le bouton d'action muet — il refuse sans rien dire. */}
            {errors[cle] && <span className="error-message">{errors[cle]}</span>}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="bcadm-desc__grid">
      <Champ col={6} label="Nom de l'organisme" valeur={org?.nom} />
      <Champ col={3} label="Siret" valeur={org?.siret} />
      <Champ col={3} label="Type d'organisme" valeur={org?.type_organisme} />
      <Champ col={12} label="Adresse du siège social" valeur={adresseLigne} />
      {/* Contact operationnel : SAISI sur l'operation, pre-rempli avec l'acheteur qui l'initie (le
          repli vit dans refonteFromOperation). Modifier ces champs n'affecte NI le compte de
          l'utilisateur NI la fiche de l'organisme — c'est le contact de CETTE operation.
          « Fonction » etait cablee sur `undefined`, donc vide en permanence. */}
      {champSaisi('contactNom', 'Nom du contact opérationnel', 'ex : Marie DUPONT', 3)}
      {champSaisi('contactFonction', 'Fonction', 'ex : Responsable travaux', 3)}
      {champSaisi('contactMail', 'Mail', 'ex : m.dupont@organisme.fr', 3)}
      {champSaisi('contactTelephone', 'Téléphone', 'ex : 01 02 03 04 05', 3)}
      {champSaisi('signataire', 'Nom du signataire qui apparaitra sur la commande', 'ex : François VINIO, Président du CHSJ')}
      {champSaisi('modaliteFacturation', 'Modalité de facturation', 'ex : envoyer vos factures sur CHORUS, avec la mention LIS456')}
    </div>
  );
};

// --- Sous-bloc "Particularites administratives" : 5 champs (parite MS, hors visite) -----------
interface ParticularitesChamp {
  cle:
    | 'niveauSensibilite'
    | 'conditionVisite'
    | 'verificationIdentites'
    | 'habilitation'
    | 'charteReglementation'
    | 'clauseSuiviEnvironnemental';
  label: string;
  placeholder: string;
  type: 'select' | 'text' | 'textarea';
  requis: boolean;
  options?: string[];
}

// Ordre et requis IDENTIQUES a MS (OperationParticularites). La visite est portee cote BC par la
// colonne `condition_visite` (cle conditionVisite) — pas de colonne dediee en doublon.
const PARTICULARITES: ParticularitesChamp[] = [
  {
    cle: 'niveauSensibilite',
    label: 'Niveau de sensibilité des informations liées à cette opération ?',
    placeholder: 'Sélectionnez…',
    type: 'select',
    requis: true,
    options: ['Public (P)', 'Diffusion restreinte (DR)', 'Secret (S)', 'Très Secret (TS)'],
  },
  {
    cle: 'conditionVisite',
    label: 'Visite préalable obligatoire ?',
    placeholder: 'ex : Non / Oui entre le 24/09/28 et le 27/09/28',
    type: 'text',
    requis: true,
  },
  {
    cle: 'verificationIdentites',
    label: "Vérification préalable des identités pour accéder à l'opération ?",
    placeholder: "ex : Non / Oui (transmettre la copie de vos pièces d'identités 48h à l'avance)",
    type: 'text',
    requis: true,
  },
  {
    cle: 'habilitation',
    label: 'Habilitation particulière à présenter ?',
    placeholder: "ex : Non / Oui (habilitation d'intervention sur site nucléaire obligatoire)",
    type: 'text',
    requis: true,
  },
  {
    cle: 'charteReglementation',
    label: 'Charte et/ou règlementation particulière imposée sur cette opération ?',
    placeholder: 'ex : Charte de gestion du bruit à respecter impérativement (voir lien URL)',
    type: 'textarea',
    requis: false,
  },
  {
    cle: 'clauseSuiviEnvironnemental',
    label: 'Clause de suivi environnemental et social ?',
    placeholder: 'ex : le tableau de suivi de la gestion des déchets doit se faire sur le fichier imposé (voir lien URL)',
    type: 'textarea',
    requis: false,
  },
];

const Particularites: React.FC<Props> = ({ formData, viewMode, errors = {}, setFormData }) => {
  const update = (cle: ParticularitesChamp['cle'], value: string) => {
    if (setFormData) setFormData({ ...formData, [cle]: value });
  };

  return (
    <div className="bcadm-desc__grid">
      {PARTICULARITES.map(({ cle, label, placeholder, type, requis, options }) => {
        const valeur = String(formData[cle] ?? '');
        const erreur = errors[cle];
        return (
          <div className="form-group" style={{ gridColumn: 'span 6' }} key={cle}>
            <label>
              {label} {!viewMode && requis && <span style={{ color: 'red' }}>*</span>}
            </label>
            {viewMode ? (
              <div className="input-inactif">{valeur || '-'}</div>
            ) : type === 'select' ? (
              <>
                <select value={valeur} onChange={(e) => update(cle, e.target.value)} className={erreur ? 'error' : ''}>
                  <option value="">{placeholder}</option>
                  {(options ?? []).map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                {erreur && <span className="error-message">{erreur}</span>}
              </>
            ) : type === 'textarea' ? (
              <>
                <textarea value={valeur} onChange={(e) => update(cle, e.target.value)} className={erreur ? 'error' : ''} placeholder={placeholder} rows={3} maxLength={PARTICULARITES_MAX} />
                <span className="bcadm-char-count">{valeur.length}/{PARTICULARITES_MAX}</span>
                {erreur && <span className="error-message">{erreur}</span>}
              </>
            ) : (
              <>
                <input type="text" value={valeur} onChange={(e) => update(cle, e.target.value)} className={erreur ? 'error' : ''} placeholder={placeholder} maxLength={PARTICULARITES_MAX} />
                <span className="bcadm-char-count">{valeur.length}/{PARTICULARITES_MAX}</span>
                {erreur && <span className="error-message">{erreur}</span>}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

// --- Wrapper : 3 sous-sections titrees (structure MS) -----------------------------------------
const DescriptifAdministratifBC: React.FC<Props> = (props) => (
  <div className="bcadm-desc">
    <section className="bcadm-desc__sous-section">
      <h4 className="bcadm-desc__sous-section-titre">Acheteur</h4>
      <AcheteurRecap {...props} />
    </section>

    <section className="bcadm-desc__sous-section">
      <h4 className="bcadm-desc__sous-section-titre">Opération</h4>
      <OperationFormulaireAdmin
        operation={props.operation}
        formData={props.formData}
        viewMode={props.viewMode}
        errors={props.errors}
        setFormData={props.setFormData}
      />
    </section>

    <section className="bcadm-desc__sous-section">
      <h4 className="bcadm-desc__sous-section-titre">Particularités administratives</h4>
      <Particularites {...props} />
    </section>
  </div>
);

export default DescriptifAdministratifBC;
